/*
  This file contains a hook that focuses on the first field with an error in a form.
  It also prevents automatic field switching when tabbing or clicking on a field.
  We problably don't to touch on this file in the future in order to give maintanability to this hook.
  The next actions regarding this portion of the code - UserInfoFormSection in question - shound't be give keep the current
  patterns and behavior but put efforts in use a better and modern way to handle forms - Use React Hook Form as preference -.
  Get error messages, error fields and do auto focus should be something simple to implement and use, but since these forms are using Redux Forms and Redux fields
  this library does not provide a good way to get error messages and error fields and due to this deadline and ticket time we got stuck with this solution.
  ticket: https://quorumanalytics.atlassian.net/browse/NEO-5886
  Epic: https://quorumanalytics.atlassian.net/browse/NEO-5876
  To reinforce, we should not need to put efforts in this file trying to improve or give maintanability, but put efforts in do a proper refactor and enhancement in the grassroots forms.
  Using React Hook Form as preference like we use in the admin, and then we may can rid with this implementation
*/

import { useCallback, useEffect, useRef } from "react"

// Common error selectors used to find fields with errors
const ERROR_SELECTORS = [
    ".has-error input",
    ".error-field input",
    "input.error",
    ".has-error textarea",
    ".error-field textarea",
    "textarea.error",
    ".has-error select",
    ".error-field select",
    "select.error",
    '[aria-invalid="true"]',
    // Redux Form specific selectors
    ".redux-form-field-error input",
    ".redux-form-field-error textarea",
    ".redux-form-field-error select",
    // Other common selectors
    ".field-error input",
    ".invalid-field input",
    // Selectors for fields with errors in Redux Form
    "input[name]:invalid",
    "textarea[name]:invalid",
    "select[name]:invalid",
]

/**
 * Hook to focus on the first field with an error in a form
 */
export const useFocusOnErrors = ({
    syncErrors,
    invalid,
    submitFailed,
    submitting,
    formSelector = "form",
    submitButtonSelectors = ['button[type="submit"], input[type="submit"]'],
    externalSubmitButtonSelectors = ['[data-cy="message-preview-submit-button"]', ".submit-button"],
}) => {
    const containerRef = useRef(null)
    const focusTimeoutRef = useRef(null)
    const isSubmitAttemptRef = useRef(false)

    // Function to focus on the first field with an error
    const focusFirstErrorField = useCallback(() => {
        // Only proceed if we have a container and this is a submit attempt
        if (!containerRef.current || !isSubmitAttemptRef.current) return

        // Clear any existing timeout
        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current)
        }

        // Set a timeout to allow the DOM to update with error messages
        focusTimeoutRef.current = setTimeout(() => {
            if (!containerRef.current) return

            // Try each selector until finding a field with an error
            for (const selector of ERROR_SELECTORS) {
                const errorFields = containerRef.current.querySelectorAll(selector)
                if (errorFields.length > 0) {
                    // Focus on the first field with an error
                    errorFields[0].focus()
                    return // Exit after finding and focusing on the first field
                }
            }

            // If no fields with errors were found using selectors, try using Redux Form errors
            if (syncErrors) {
                const errorKeys = Object.keys(syncErrors.toJS ? syncErrors.toJS() : syncErrors)
                if (errorKeys.length > 0) {
                    // Try to find the field by name
                    for (const fieldName of errorKeys) {
                        const field = containerRef.current.querySelector(
                            `[name="${fieldName}"], [data-cy="${fieldName}"]`,
                        )
                        if (field) {
                            field.focus()
                            return
                        }
                    }
                }
            }

            // Reset the submit attempt flag after focusing
            isSubmitAttemptRef.current = false
        }, 100)
    }, [syncErrors])

    // Handle form submission
    const handleFormSubmit = useCallback(() => {
        // Set the submit attempt flag
        isSubmitAttemptRef.current = true

        // Focus on the first error field if the form is invalid
        if (invalid) {
            focusFirstErrorField()
        }
    }, [invalid, focusFirstErrorField])

    // Listen for form submission events from buttons inside the form
    useEffect(() => {
        // Find all submit buttons in the form
        const findSubmitButtons = () => {
            if (!containerRef.current) return []

            const form = containerRef.current.closest(formSelector)
            if (!form) return []

            // Find all submit buttons in the form
            return Array.from(form.querySelectorAll(submitButtonSelectors.join(", ")))
        }

        const submitButtons = findSubmitButtons()

        // Add click event listeners to all submit buttons
        submitButtons.forEach((button) => {
            button.addEventListener("click", handleFormSubmit)
        })

        return () => {
            // Remove click event listeners from all submit buttons
            submitButtons.forEach((button) => {
                button.removeEventListener("click", handleFormSubmit)
            })
        }
    }, [formSelector, submitButtonSelectors, handleFormSubmit])

    // Listen for Redux Form submission events
    useEffect(() => {
        // This effect handles the case where submitFailed becomes true
        // This happens when a form is submitted and fails validation
        if (submitFailed && invalid && !submitting) {
            // Set the submit attempt flag
            isSubmitAttemptRef.current = true
            handleFormSubmit()
        }
    }, [submitFailed, invalid, submitting, handleFormSubmit])

    // Add a script to the page to handle external submit buttons
    useEffect(() => {
        // Create a script element to inject our focus handler
        const script = document.createElement("script")
        const selectors = externalSubmitButtonSelectors
            .map((selector) => {
                if (selector.startsWith("[")) {
                    return `e.target.matches('${selector}')`
                } else if (selector.startsWith(".")) {
                    return `e.target.classList.contains('${selector.substring(1)}')`
                } else if (selector.startsWith("#")) {
                    return `e.target.id === '${selector.substring(1)}'`
                } else if (selector.startsWith("data-")) {
                    const attr = selector.split("=")[0]
                    const value = selector.split("=")[1]?.replace(/['"]/g, "")
                    return value
                        ? `e.target.getAttribute('${attr}') === '${value}'`
                        : `e.target.hasAttribute('${attr}')`
                } else {
                    return `e.target.getAttribute('data-cy') === '${selector}'`
                }
            })
            .join(" || ")

        script.textContent = `
            // Find all external submit buttons for the form
            document.addEventListener('click', function(e) {
                // Check if the clicked element is a submit button for our form
                if (e.target && (${selectors})) {
                    // Dispatch a custom event to notify the React component
                    document.dispatchEvent(new CustomEvent('externalSubmit'));
                }
            });
        `

        // Add the script to the page
        document.head.appendChild(script)

        // Listen for the custom event
        const handleExternalSubmit = () => {
            // Set the submit attempt flag
            isSubmitAttemptRef.current = true
            handleFormSubmit()
        }

        document.addEventListener("externalSubmit", handleExternalSubmit)

        return () => {
            // Remove the script and event listener when the component unmounts
            document.head.removeChild(script)
            document.removeEventListener("externalSubmit", handleExternalSubmit)
        }
    }, [externalSubmitButtonSelectors, handleFormSubmit])

    // Inject script to prevent automatic field switching
    useEffect(() => {
        // Create a script element
        const script = document.createElement("script")
        script.id = "prevent-auto-focus-script"

        // Script content to prevent automatic field switching
        script.textContent = `
            (function() {
                // Keep track of the current active element
                let lastActiveElement = null;
                let isUserAction = false;
                let isSubmitting = false;
                // Capture the active element when it receives focus
                document.addEventListener('focusin', function(e) {
                    // Check if e.target is an Element before calling closest()
                    if (!e.target || typeof e.target.closest !== 'function') {
                        return;
                    }
                    
                    // Check if the target is part of a select component (react-select or native select)
                    const isSelectElement = e.target.closest('.react-select__control') ||
                                          e.target.closest('.react-select__menu') ||
                                          e.target.closest('.react-select__option') ||
                                          e.target.closest('.react-select__input') ||
                                          e.target.closest('.react-select__value-container') ||
                                          e.target.closest('.quorum-reactselect-wrapper') ||
                                          e.target.closest('select') ||
                                          e.target.tagName === 'SELECT' ||
                                          e.target.getAttribute('role') === 'combobox' ||
                                          e.target.getAttribute('role') === 'listbox' ||
                                          e.target.getAttribute('role') === 'option';

                    // If this is a user action (click or tab) or form submission, or select element, allow it
                    if (isUserAction || isSubmitting || isSelectElement) {
                        lastActiveElement = e.target;
                    }
                    // Otherwise, if we have a previous element and this is not user-initiated,
                    // prevent the focus change by returning focus to the previous element
                    else if (lastActiveElement && lastActiveElement !== e.target) {
                        // Prevent the default focus behavior
                        e.preventDefault();
                        e.stopPropagation();
                        // Return focus to the previous element
                        setTimeout(() => {
                            if (lastActiveElement && document.contains(lastActiveElement)) {
                                lastActiveElement.focus({preventScroll: true});
                            }
                        }, 0);
                    }
                    // If there's no previous element, allow this focus and record it
                    else {
                        lastActiveElement = e.target;
                    }
                    // Reset the user action flag
                    isUserAction = false;
                }, true);
                // Detect user-initiated focus changes (mouse clicks)
                document.addEventListener('mousedown', function(e) {
                    isUserAction = true;
                    // Also check if clicking on select-related elements
                    const isSelectClick = (e.target && typeof e.target.closest === 'function') &&
                                        (e.target.closest('.react-select__control') ||
                                        e.target.closest('.react-select__dropdown-indicator') ||
                                        e.target.closest('.react-select__clear-indicator') ||
                                        e.target.closest('.quorum-reactselect-wrapper') ||
                                        e.target.closest('select')) ||
                                        e.target.tagName === 'SELECT';
                    if (isSelectClick) {
                        // For select clicks, keep the user action flag longer
                        setTimeout(() => {
                            isUserAction = false;
                        }, 200);
                        return;
                    }
                }, true);
                // Detect user-initiated focus changes (keyboard tab)
                document.addEventListener('keydown', function(e) {
                    if (e.key === 'Tab' || e.key === 'Enter' || e.key === 'Space' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        isUserAction = true;
                    }
                }, true);
                // Detect form submissions
                document.addEventListener('submit', function() {
                    isSubmitting = true;
                    // Reset after a delay
                    setTimeout(() => {
                        isSubmitting = false;
                    }, 500);
                }, true);
                // Detect clicks on submit buttons
                document.addEventListener('click', function(e) {
                    if (e.target &&
                        (e.target.type === 'submit' ||
                         (typeof e.target.closest === 'function' && e.target.closest('button[type="submit"]')) ||
                         (typeof e.target.closest === 'function' && e.target.closest('input[type="submit"]')))) {
                        isSubmitting = true;
                        // Reset after a delay
                        setTimeout(() => {
                            isSubmitting = false;
                        }, 500);
                    }
                }, true);
                // Disable autocomplete on all forms and inputs
                function disableAutocomplete() {
                    document.querySelectorAll('form').forEach(form => {
                        form.setAttribute('autocomplete', 'off');
                        form.setAttribute('novalidate', 'novalidate');
                    });
                    document.querySelectorAll('input, textarea, select').forEach(input => {
                        input.setAttribute('autocomplete', 'off');
                    });
                }
                // Run immediately
                disableAutocomplete();
                // Also run for dynamically added elements
                const observer = new MutationObserver(function(mutations) {
                    mutations.forEach(function(mutation) {
                        if (mutation.addedNodes.length) {
                            disableAutocomplete();
                        }
                    });
                });
                // Start observing
                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });
                // Prevent browser's automatic field validation focus
                const originalCheckValidity = HTMLFormElement.prototype.checkValidity;
                HTMLFormElement.prototype.checkValidity = function() {
                    // Set the submitting flag to allow focus changes during validation
                    isSubmitting = true;
                    // Call the original method
                    const result = originalCheckValidity.apply(this, arguments);
                    // Reset the flag after a delay
                    setTimeout(() => {
                        isSubmitting = false;
                    }, 500);
                    return result;
                };
                // Prevent browser's automatic field validation focus
                const originalReportValidity = HTMLFormElement.prototype.reportValidity;
                HTMLFormElement.prototype.reportValidity = function() {
                    // Set the submitting flag to allow focus changes during validation
                    isSubmitting = true;
                    // Call the original method
                    const result = originalReportValidity.apply(this, arguments);
                    // Reset the flag after a delay
                    setTimeout(() => {
                        isSubmitting = false;
                    }, 500);
                    return result;
                };
                // Also override individual input validation methods
                const originalInputCheckValidity = HTMLInputElement.prototype.checkValidity;
                HTMLInputElement.prototype.checkValidity = function() {
                    // Remember the current active element
                    const currentActive = document.activeElement;
                    // Call the original method
                    const result = originalInputCheckValidity.apply(this, arguments);
                    // If focus changed during validation and this is not a submission,
                    // restore focus to the previous element
                    if (!isSubmitting && currentActive && currentActive !== document.activeElement) {
                        setTimeout(() => {
                            if (currentActive && document.contains(currentActive)) {
                                currentActive.focus({preventScroll: true});
                            }
                        }, 0);
                    }
                    return result;
                };
                // Override input reportValidity
                const originalInputReportValidity = HTMLInputElement.prototype.reportValidity;
                HTMLInputElement.prototype.reportValidity = function() {
                    // Remember the current active element
                    const currentActive = document.activeElement;
                    // Call the original method
                    const result = originalInputReportValidity.apply(this, arguments);
                    // If focus changed during validation and this is not a submission,
                    // restore focus to the previous element
                    if (!isSubmitting && currentActive && currentActive !== document.activeElement) {
                        setTimeout(() => {
                            if (currentActive && document.contains(currentActive)) {
                                currentActive.focus({preventScroll: true});
                            }
                        }, 0);
                    }
                    return result;
                };
                // Prevent automatic focus changes from input events
                document.addEventListener('input', function(e) {
                    // Check if the input event is from a select element
                    const isSelectInput = (e.target && typeof e.target.closest === 'function') &&
                                        (e.target.closest('.react-select__control') ||
                                        e.target.closest('.react-select__input') ||
                                        e.target.closest('.quorum-reactselect-wrapper') ||
                                        e.target.closest('select')) ||
                                        e.target.tagName === 'SELECT';

                    // Don't interfere with select input events
                    if (isSelectInput) {
                        return;
                    }

                    // Remember the current active element
                    const currentActive = document.activeElement;
                    // Check after a short delay if focus has changed
                    setTimeout(() => {
                        // If focus changed during input and this is not a user action or submission,
                        // restore focus to the previous element
                        if (!isUserAction && !isSubmitting &&
                            currentActive &&
                            currentActive !== document.activeElement) {
                            if (currentActive && document.contains(currentActive)) {
                                currentActive.focus({preventScroll: true});
                            }
                        }
                    }, 0);
                }, true);
                // Prevent automatic focus changes from change events
                document.addEventListener('change', function(e) {
                    // Check if the change event is from a select element
                    const isSelectChange = (e.target && typeof e.target.closest === 'function') &&
                                         (e.target.closest('.react-select__control') ||
                                         e.target.closest('.react-select__input') ||
                                         e.target.closest('.quorum-reactselect-wrapper') ||
                                         e.target.closest('select')) ||
                                         e.target.tagName === 'SELECT';

                    // Don't interfere with select change events
                    if (isSelectChange) {
                        return;
                    }

                    // Remember the current active element
                    const currentActive = document.activeElement;
                    // Check after a short delay if focus has changed
                    setTimeout(() => {
                        // If focus changed during change event and this is not a user action or submission,
                        // restore focus to the previous element
                        if (!isUserAction && !isSubmitting &&
                            currentActive &&
                            currentActive !== document.activeElement) {
                            if (currentActive && document.contains(currentActive)) {
                                currentActive.focus({preventScroll: true});
                            }
                        }
                    }, 0);
                }, true);
            })();
        `

        // Add the script to the page if it doesn't already exist
        if (!document.getElementById("prevent-auto-focus-script")) {
            document.head.appendChild(script)
        }

        return () => {
            // Remove the script when the component unmounts
            const existingScript = document.getElementById("prevent-auto-focus-script")
            if (existingScript) {
                document.head.removeChild(existingScript)
            }
        }
    }, [])

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current)
            }
        }
    }, [])

    return containerRef
}
